import React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  ChangeTemplateDto,
  ClientGpDto,
  ContactDto,
  CostCodeDto,
  DivisionDto,
  OrganizationOfficeDto,
  OrganizationRoleDto,
  PayCodeDto,
  UserDto,
} from 'src/api/FiretrolApiClient';
import { queryKeys } from 'src/api/queries';
import { useRoles } from 'src/api/queries/organization/useRoles';
import { useAdminUsers } from 'src/api/queries/organization/useAdminUsers';
import { useOffices } from 'src/api/queries/organization/useOffices';
import { useChangeTemplates } from 'src/api/queries/organization/useChangeTemplates';
import { useContacts } from 'src/api/queries/organization/useContacts';
import { useDivisions } from 'src/api/queries/organization/useDivisions';
import { useCostCodes } from 'src/api/queries/organization/useCostCodes';
import { usePayCodes } from 'src/api/queries/organization/usePayCodes';
import { useClients } from 'src/api/queries/organization/useClients';
import { getErrorMessage, showErrorToast } from 'src/utils';
import { PropsWithChildren } from 'src/PropsWithChildren';

interface IOrganizationContext {
  roles: OrganizationRoleDto[];
  offices: OrganizationOfficeDto[];
  changeTemplates: ChangeTemplateDto[];
  users: UserDto[];
  divisions: DivisionDto[];
  clients: ClientGpDto[];
  contacts: ContactDto[];
  costCodes: CostCodeDto[];
  payCodes: PayCodeDto[];
  loading: boolean;
  clientsLoading: boolean;
  costCodesLoading: boolean;
  payCodesLoading: boolean;
  refreshUsers: () => Promise<void>;
  refreshPayCodes: () => Promise<void>;
}

export const OrganizationContext = React.createContext<IOrganizationContext>({
  loading: true,
  clientsLoading: true,
  costCodesLoading: true,
  payCodesLoading: true,
  roles: [],
  users: [],
  offices: [],
  changeTemplates: [],
  clients: [],
  contacts: [],
  divisions: [],
  costCodes: [],
  payCodes: [],
  refreshUsers: () => Promise.resolve(),
  refreshPayCodes: () => Promise.resolve(),
});

/**
 * Strangler shim: consumers still read the nine organization slices through
 * this context, but the body delegates to the react-query hooks in
 * `src/api/queries/organization/*`. Writes no longer go through here — each
 * mutation hook declares its own invalidations.
 *
 * - The 9 slices this used to load with sequential `await`s now load in
 *   parallel, each as its own independent query.
 * - Loads are fail-soft: a slice failing to load never throws out of the
 *   provider (it just leaves that slice's array empty), matching legacy
 *   behavior — failures still reach the central `QueryCache.onError`
 *   pipeline for logging/toasting.
 * - `refresh*` calls are user-initiated, so unlike loads they surface
 *   failures directly via a toast (see the refresh functions below).
 * - `loading` / `costCodesLoading` / `payCodesLoading` are mapped to
 *   preserve the legacy loading windows exactly (see the `loading`
 *   computation below), including one that isn't obvious from the interface:
 *   `usersQ.isFetching` (not `isLoading`) so that legacy `refreshUsers`'s
 *   behavior of flipping the shared `loading` flag during a manual refresh
 *   is preserved for the one consumer that depends on it (the Users settings
 *   page binds its table's `loading` to this and calls `refreshUsers` from
 *   its Refresh button).
 */
export const OrganizationProvider: React.FC<PropsWithChildren> = ({
  children,
}) => {
  const qc = useQueryClient();

  const rolesQ = useRoles();
  const usersQ = useAdminUsers();
  const officesQ = useOffices();
  const changeTemplatesQ = useChangeTemplates();
  const contactsQ = useContacts();
  const divisionsQ = useDivisions();
  const costCodesQ = useCostCodes();
  const payCodesQ = usePayCodes();
  const clientsQ = useClients();

  // Legacy `loading` covered organization/roles/users/offices/
  // changeTemplates/contacts/divisions/costCodes/payCodes (clients excluded,
  // it always had its own flag). `usersQ.isFetching` (not `isLoading`)
  // reproduces legacy `refreshUsers` flipping the shared `loading` flag
  // during a manual refresh, not just the initial load.
  const loading =
    rolesQ.isLoading ||
    usersQ.isFetching ||
    officesQ.isLoading ||
    changeTemplatesQ.isLoading ||
    contactsQ.isLoading ||
    divisionsQ.isLoading ||
    costCodesQ.isLoading ||
    payCodesQ.isLoading;

  // Unlike loads (fail-soft, logged only), a refresh is user-initiated, so
  // its failure is toasted here. The QueryCache.onError pipeline has already
  // logged it by the time this catch runs — this only adds the toast.
  const refreshUsers = async () => {
    try {
      await qc.invalidateQueries(
        { queryKey: queryKeys.organization.adminUsers() },
        { throwOnError: true },
      );
    } catch (err) {
      showErrorToast(getErrorMessage(err));
    }
  };
  const refreshPayCodes = async () => {
    try {
      await qc.invalidateQueries(
        { queryKey: queryKeys.payCodes.list() },
        { throwOnError: true },
      );
    } catch (err) {
      showErrorToast(getErrorMessage(err));
    }
  };

  return (
    <OrganizationContext.Provider
      value={{
        loading,
        clientsLoading: clientsQ.isLoading,
        costCodesLoading: costCodesQ.isFetching,
        payCodesLoading: payCodesQ.isFetching,
        roles: rolesQ.data ?? [],
        users: usersQ.data ?? [],
        offices: officesQ.data ?? [],
        changeTemplates: changeTemplatesQ.data ?? [],
        contacts: contactsQ.data ?? [],
        divisions: divisionsQ.data ?? [],
        costCodes: costCodesQ.data ?? [],
        payCodes: payCodesQ.data ?? [],
        clients: clientsQ.data ?? [],
        refreshUsers,
        refreshPayCodes,
      }}
    >
      {children}
    </OrganizationContext.Provider>
  );
};

export const useOrganization: () => IOrganizationContext = () => {
  const context = React.useContext(OrganizationContext);
  return context;
};
